/* @flow */

import * as React from 'react';
import Select, {Async} from 'react-select';

import {SelectPickerBase} from './select-picker-base';
import {SelectPickerCreatableWrapper} from './select-picker-creatable-wrapper';

/*
 * This component is the exported shell that allows our create-forms
 * (and share forms) to render a select with additional properties.
 *
 * Because of how react-select is setup, there's a lot of "magic" that happens
 * based on the presence of different properties. This component acts
 * as the entry-point for our consuming components, and it takes care of the
 * rest of the logic.
 */

type Props = {
    name: string,
    label: string,
    // Purposefully loosely typed, this breaks everything otherwise
    // This component is always rendered by a stricter typed parent
    //
    // Technically, with multi it is:
    // `onChange: (selectedOptions: Object[]) => any`
    //
    // and non-multi:
    // `onChange: (selectedOption: ?Object`) => any`
    onChange: Function,

    /** Similarly with onChange, this value can either be one of two
    types, depending on the `multi` prop.

    If multi is true:
    `value: Object[]`

    and non-multi:
    `value: ?Object` */
    value: any,
    valueKey: string,
    labelKey: string,

    /** This signifies an Async, multi-select */
    loadOptions?: (query: string) => Promise<Object[]>,

    /** These are only required if loadOptions is not present */
    options?: Object[],
    multi: boolean,

    truncateMultiOptions?: boolean,

    clearable?: boolean,

    /** This props allows for creation of non-available entities,
    must be combined with the following 3 props */
    allowCreate?: boolean,
    creatableCollections?: string[],
    onCreate?: ({
        value: string,
        entityType: string,
    }) => any,

    autoBlur?: boolean,
};

/**
 * Pretty much just used in the entity creator modals.
 */
export class SelectPicker extends React.PureComponent<Props> {
    static defaultProps = {
        valueKey: 'id',
        labelKey: 'name',
    };

    render() {
        const {allowCreate, creatableCollections, onCreate, ...otherProps} = this.props;
        if (allowCreate && creatableCollections && onCreate) {
            return (
                <SelectPickerCreatableWrapper
                    {...otherProps}
                    allowCreate={allowCreate}
                    creatableCollections={creatableCollections}
                    onCreate={onCreate}
                />
            );
        }

        const component = this.props.loadOptions ? Async : Select;

        return <SelectPickerBase {...this.props} component={component} />;
    }
}
